import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import { MONTH_ORDER, type TrialBalanceRow } from '../profitAndLoss/constants';
import type { LedgerCellContext } from './JournalLedgerDialog';

type Props = {
  rows: TrialBalanceRow[];
  expandedSections: Record<string, boolean>;
  toggleSection: (sectionName: string) => void;
  expandedAccounts: Record<string, boolean>;
  toggleAccount: (accountCode: string) => void;
  // 金額セルクリックで補助元帳ダイアログを開く（ページ側が状態を保持）
  onCellClick?: (ctx: LedgerCellContext) => void;
};

// 金額セルを「補助元帳ドリルダウン可能」に見せる共通スタイル
const clickableAmountSx = {
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#e0e7ff', textDecoration: 'underline' },
} as const;

// 金額セル表示 (0 は '-'、それ以外は3桁カンマ)
const fmt = (v: number): string => (v !== 0 ? Math.round(v).toLocaleString() : '-');

// 該当行の指定月の発生額 (month_{m}_actual)
const monthVal = (row: TrialBalanceRow, m: number): number => Number(row[`month_${m}_actual`]) || 0;

// 月ごとの合算ヘルパー
type MonthMap = Record<number, number>;
const addMonths = (target: MonthMap, row: TrialBalanceRow) => {
  MONTH_ORDER.forEach(m => { target[m] = (target[m] || 0) + monthVal(row, m); });
};
const sumMonths = (mm: MonthMap): number => MONTH_ORDER.reduce((s, m) => s + (mm[m] || 0), 0);
// 全月ゼロ判定 (OBC の試算表は発生のない補助科目を表示しないため、廃止済み旧コード等の空行を隠す)
const hasAnyMonthValue = (mm: MonthMap): boolean => MONTH_ORDER.some(m => (mm[m] || 0) !== 0);

/**
 * 月次合計試算表テーブル（科目区分→勘定科目→補助科目のツリービュー）。
 *
 * 各勘定科目について 9〜8月の発生額を12列で横並びにし、右端に通期累計を表示する月次推移表。
 * 勘定科目行をクリックすると配下の補助科目内訳（補助元帳の段階1）を開閉する。
 * OBC 実績は補助なし科目も sub_account_code='0' (その他) の「補」行として返るため、
 * account_code 単位の合算がそのまま科目合計になり、二重カウントは起きない。
 */
export default function MonthlyTrialBalanceTable({
  rows,
  expandedSections,
  toggleSection,
  expandedAccounts,
  toggleAccount,
  onCellClick,
}: Props) {
  // 月セルの共通スタイル
  const monthHeadSx = { backgroundColor: '#f5f5f5', color: 'grey.500', fontWeight: 500, fontSize: '0.75rem', whiteSpace: 'nowrap', minWidth: '78px', zIndex: 1 } as const;
  const totalHeadSx = { backgroundColor: '#eef2ff', color: '#3730a3', fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap', minWidth: '96px', zIndex: 1 } as const;

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgb(0 0 0 / 0.12)', overflow: 'auto', flex: 1, minHeight: 0 }}>
        <Table stickyHeader size="small" sx={{ width: '100%', minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#f5f5f5', color: 'grey.500', fontWeight: 500, fontSize: '0.8125rem', whiteSpace: 'nowrap', minWidth: '220px', position: 'sticky', left: 0, zIndex: 11 }}>
                科目区分 / 勘定科目
              </TableCell>
              {MONTH_ORDER.map(m => (
                <TableCell key={m} align="right" sx={monthHeadSx}>{m}月</TableCell>
              ))}
              <TableCell align="right" sx={totalHeadSx}>通期累計</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              // 1. 科目区分 (category_l2) ごとにグループ化 (API の ORDER BY で並び順は維持済み)
              const sectionOrder: string[] = [];
              const sectionMap: Record<string, TrialBalanceRow[]> = {};
              rows.forEach(row => {
                const name = row.category_l2_name || 'その他';
                if (!sectionMap[name]) {
                  sectionMap[name] = [];
                  sectionOrder.push(name);
                }
                sectionMap[name].push(row);
              });

              return sectionOrder.map(sectionName => {
                const groupRows = sectionMap[sectionName];

                // 2. 勘定科目 (科) 単位に集約 + 補助科目 (補) 内訳。いずれも月次マップで保持
                const accountAgg: Record<string, {
                  name: string;
                  months: MonthMap;
                  subAccounts: Record<string, { name: string; months: MonthMap }>;
                  subOrder: string[];
                }> = {};
                const accountOrder: string[] = [];

                groupRows.forEach(row => {
                  const accCode = row.account_code || 'unknown';
                  const subAccCode = row.sub_account_code || '';
                  if (!accountAgg[accCode]) {
                    accountAgg[accCode] = { name: row.account_name || '-', months: {}, subAccounts: {}, subOrder: [] };
                    accountOrder.push(accCode);
                  }
                  addMonths(accountAgg[accCode].months, row);

                  if (subAccCode) {
                    if (!accountAgg[accCode].subAccounts[subAccCode]) {
                      accountAgg[accCode].subAccounts[subAccCode] = { name: row.sub_account_name || 'その他', months: {} };
                      accountAgg[accCode].subOrder.push(subAccCode);
                    }
                    addMonths(accountAgg[accCode].subAccounts[subAccCode].months, row);
                  }
                });

                // 3. 科目区分合計（月次）
                const sectionMonths: MonthMap = {};
                accountOrder.forEach(code => {
                  MONTH_ORDER.forEach(m => { sectionMonths[m] = (sectionMonths[m] || 0) + (accountAgg[code].months[m] || 0); });
                });

                const isSectionExpanded = !!expandedSections[sectionName];

                return (
                  <React.Fragment key={sectionName}>
                    {/* 科目区分行 (クリックで開閉) */}
                    <TableRow
                      onClick={() => toggleSection(sectionName)}
                      sx={{ backgroundColor: '#ffffff', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                    >
                      <TableCell sx={{ fontWeight: 800, color: '#334155', position: 'sticky', left: 0, zIndex: 9, backgroundColor: '#ffffff', borderBottom: '1px solid #f1f5f9' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton size="small" sx={{ mr: 1, p: 0.5, color: '#94a3b8' }}>
                            {isSectionExpanded ? <Icons.ExpandMore fontSize="small" /> : <Icons.KeyboardArrowRight fontSize="small" />}
                          </IconButton>
                          {sectionName.replace(/計$/, '')}
                        </Box>
                      </TableCell>
                      {MONTH_ORDER.map(m => (
                        <TableCell key={m} align="right" sx={{ fontWeight: 700, fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}>
                          {fmt(sectionMonths[m] || 0)}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '13px', color: '#3730a3', backgroundColor: '#eef2ff', borderBottom: '1px solid #f1f5f9' }}>
                        {fmt(sumMonths(sectionMonths))}
                      </TableCell>
                    </TableRow>

                    {/* 勘定科目行 + 補助科目内訳 */}
                    {isSectionExpanded && accountOrder.map(code => {
                      const data = accountAgg[code];
                      // 当年の発生が全月ゼロの補助科目 (廃止済み旧コード等) は表示しない
                      const visibleSubOrder = data.subOrder.filter(subCode => hasAnyMonthValue(data.subAccounts[subCode].months));
                      const hasSubAccounts = visibleSubOrder.length > 0;
                      const isAccExpanded = !!expandedAccounts[code];

                      return (
                        <React.Fragment key={code}>
                          <TableRow
                            hover
                            onClick={() => hasSubAccounts && toggleAccount(code)}
                            sx={{ '&:hover': { backgroundColor: '#f5f5f5' }, backgroundColor: '#ffffff', cursor: hasSubAccounts ? 'pointer' : 'default' }}
                          >
                            <TableCell sx={{ fontWeight: 500, color: '#475569', position: 'sticky', left: 0, zIndex: 9, backgroundColor: '#ffffff', pl: 4, borderBottom: '1px dashed #f1f5f9', whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {hasSubAccounts ? (
                                  <IconButton size="small" sx={{ mr: 1, p: 0.5, color: '#94a3b8' }}>
                                    {isAccExpanded ? <Icons.ExpandMore fontSize="small" /> : <Icons.KeyboardArrowRight fontSize="small" />}
                                  </IconButton>
                                ) : (
                                  <Box sx={{ width: 28, height: 28, mr: 1 }} />
                                )}
                                {code !== 'unknown' && (
                                  <Box component="span" sx={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace', mr: 1 }}>
                                    {code}
                                  </Box>
                                )}
                                {data.name}
                              </Box>
                            </TableCell>
                            {MONTH_ORDER.map(m => (
                              <TableCell
                                key={m}
                                align="right"
                                onClick={(e) => { e.stopPropagation(); onCellClick?.({ accountCode: code, accountName: data.name, month: m, amount: data.months[m] || 0 }); }}
                                sx={{ fontSize: '13px', fontWeight: 600, borderBottom: '1px dashed #f1f5f9', ...clickableAmountSx }}
                              >
                                {fmt(data.months[m] || 0)}
                              </TableCell>
                            ))}
                            <TableCell
                              align="right"
                              onClick={(e) => { e.stopPropagation(); onCellClick?.({ accountCode: code, accountName: data.name, month: null, amount: sumMonths(data.months) }); }}
                              sx={{ fontSize: '13px', fontWeight: 700, color: '#3730a3', backgroundColor: '#f5f7ff', borderBottom: '1px dashed #f1f5f9', ...clickableAmountSx }}
                            >
                              {fmt(sumMonths(data.months))}
                            </TableCell>
                          </TableRow>

                          {hasSubAccounts && isAccExpanded && visibleSubOrder.map(subCode => {
                            const subData = data.subAccounts[subCode];
                            return (
                              <TableRow key={`${code}-${subCode}`} hover sx={{ '&:hover': { backgroundColor: '#f5f5f5' }, backgroundColor: '#fbfdff' }}>
                                <TableCell sx={{ fontSize: '12.5px', color: '#64748b', pl: 10, position: 'sticky', left: 0, zIndex: 9, backgroundColor: '#fbfdff', borderBottom: '1px dashed #f1f5f9', whiteSpace: 'nowrap' }}>
                                  <Box component="span" sx={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace', mr: 1 }}>
                                    {subCode}
                                  </Box>
                                  {subData.name}
                                </TableCell>
                                {MONTH_ORDER.map(m => (
                                  <TableCell
                                    key={m}
                                    align="right"
                                    onClick={(e) => { e.stopPropagation(); onCellClick?.({ accountCode: code, accountName: data.name, subAccountCode: subCode, subAccountName: subData.name, month: m, amount: subData.months[m] || 0 }); }}
                                    sx={{ fontSize: '12.5px', color: '#475569', borderBottom: '1px dashed #f1f5f9', ...clickableAmountSx }}
                                  >
                                    {fmt(subData.months[m] || 0)}
                                  </TableCell>
                                ))}
                                <TableCell
                                  align="right"
                                  onClick={(e) => { e.stopPropagation(); onCellClick?.({ accountCode: code, accountName: data.name, subAccountCode: subCode, subAccountName: subData.name, month: null, amount: sumMonths(subData.months) }); }}
                                  sx={{ fontSize: '12.5px', fontWeight: 600, color: '#3730a3', backgroundColor: '#f5f7ff', borderBottom: '1px dashed #f1f5f9', ...clickableAmountSx }}
                                >
                                  {fmt(sumMonths(subData.months))}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              });
            })()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
